import { useCallback, useEffect, useRef, useState } from 'react';

export interface BarRacePlayer {
  /** 0..1 */
  progress: number;
  playing: boolean;
  speed: number;
  finished: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Back to the start and play */
  restart: () => void;
  seek: (progress: number) => void;
  setSpeed: (speed: number) => void;
}

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Playback clock of a bar race. `resetKey` (for example the round id) starts it again from zero when
 * it changes; it only runs once `ready` (the data arrived). With reduced motion it starts finished.
 */
export function useBarRacePlayer(options: { playbackMs: number; ready: boolean; resetKey?: string }): BarRacePlayer {
  const { playbackMs, ready, resetKey } = options;
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const last = useRef(0);

  useEffect(() => {
    if (!ready) return;
    if (reducedMotion()) {
      setProgress(1);
      setPlaying(false);
    } else {
      setProgress(0);
      setPlaying(true);
    }
  }, [ready, resetKey]);

  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    last.current = performance.now();
    const tick = (now: number) => {
      const dt = now - last.current;
      last.current = now;
      setProgress((p) => {
        const next = p + (dt * speed) / playbackMs;
        if (next >= 1) {
          setPlaying(false);
          return 1;
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, playbackMs]);

  const play = useCallback(() => setPlaying(true), []);
  const pause = useCallback(() => setPlaying(false), []);
  const restart = useCallback(() => {
    setProgress(0);
    setPlaying(true);
  }, []);
  const toggle = useCallback(() => {
    setProgress((p) => (p >= 1 ? 0 : p));
    setPlaying((v) => !v);
  }, []);
  const seek = useCallback((p: number) => {
    setPlaying(false);
    setProgress(Math.min(1, Math.max(0, p)));
  }, []);

  return { progress, playing, speed, finished: progress >= 1, play, pause, toggle, restart, seek, setSpeed };
}
